/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1693180800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Aug 28 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "segment",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Segment",
          "depth": 0,
          "id": "connect-to-segment"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "User and anonymous IDs",
          "depth": 1,
          "id": "user-and-anonymous-ids"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Sending initial results (Track events only)",
          "depth": 1,
          "id": "sending-initial-results-track-events-only"
        }, {
          "value": "Event names and timestamps (Generic track events only)",
          "depth": 1,
          "id": "event-names-and-timestamps-generic-track-events-only"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      ol: "ol",
      li: "li",
      img: "img",
      h3: "h3",
      ul: "ul",
      h4: "h4",
      code: "code"
    }, _provideComponents(), props.components), {EventSyncWarning, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Besides the ability to track first-party customer data, Segment offers robust integrations with the most popular SaaS tools. With our Segment integration, you can automatically sync enriched customer data from your data warehouse with Segment to send this data to downstream tools that Hightouch doesn't already integrate with."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Track (Generic) events"
              })
            }), _jsx(_components.td, {
              children: "Sends events of different names when a record is added, changed, or removed in the query results"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://segment.com/docs/connections/spec/track/",
                children: "Track specs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Track (Segment Entered / Exited) events"
              })
            }), _jsx(_components.td, {
              children: "Sends the event \"Segment Entered\" when a record is added and \"Segment Existed\" when a record is removed"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://segment.com/docs/connections/spec/track/",
                children: "Track specs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Identify events"
              })
            }), _jsx(_components.td, {
              children: "Sends an identify event with related properties of the user"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://segment.com/docs/connections/spec/identify/",
                children: "Identify specs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Group events"
              })
            }), _jsx(_components.td, {
              children: "Sends a group event to associate a user to a group"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://segment.com/docs/connections/spec/group/",
                children: "Group specs"
              })
            })]
          })]
        })]
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-segment",
        children: "Connect to Segment"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Segment"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to Segment using a Segment ", _jsx(_components.strong, {
          children: "Write Key"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "To retrieve the write key for your Segment destination in Hightouch:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Create a new HTTP API source in Segment using ", _jsx(_components.a, {
            href: "https://segment.com/docs/connections/sources/#create-a-source",
            children: "these instructions"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Navigate to ", _jsx(_components.strong, {
            children: "Source Settings > API Keys"
          }), " and copy the ", _jsx(_components.strong, {
            children: "Write Key"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-segment-write-key.png",
          alt: "Write Key in Segment UI"
        })
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Segment destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Segment destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "user-and-anonymous-ids",
        children: "User and anonymous IDs"
      }), "\n", _jsx(_components.p, {
        children: "You need to include send either the user or anonymous ID to Segment to identify the user associated with the event."
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source into user (identify) or event (track) properties."
      }), "\n", _jsx(_components.h3, {
        id: "sending-initial-results-track-events-only",
        children: "Sending initial results (Track events only)"
      }), "\n", _jsx(_components.p, {
        children: "For the two track event types, Hightouch lets you choose whether to:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Send the initial set of results as track events as Entered / Added events"
        }), "\n", _jsx(_components.li, {
          children: "Or, only send the changes between the results in the subsequent syncs as track events"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Checking the ", _jsx(_components.strong, {
          children: "Send events for initial result set"
        }), " checkbox results in a large amount of events being sent in the first sync."]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch always send the initial results set when sending Identify and Group events."
      }), "\n", _jsx(_components.h3, {
        id: "event-names-and-timestamps-generic-track-events-only",
        children: "Event names and timestamps (Generic track events only)"
      }), "\n", _jsx(_components.p, {
        children: "For the generic track mode, there are further settings that are needed to determine which event name Hightouch sends to Segment for each added, changed, and removed record."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-segment-event-names.png",
          alt: "Setting event names in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "In the preceding screenshot, Hightouch sends Segment:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "A \"User Signed Up\" event when a record is added to the query results"
        }), "\n", _jsx(_components.li, {
          children: "A \"User Changed Subscription\" event when a record is changed in the query results"
        }), "\n", _jsx(_components.li, {
          children: "A \"User Deleted Account\" event when a record is removed in the query results"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to send a timestamp that the event occurred for generic track events. This is useful for backfilling historical events."
      }), "\n", _jsx(_components.p, {
        children: "Hightouch also lets you to send a message ID for each record to ensure that the event is deduplicated within Segment in the case that the same event erroneously gets sent twice."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "large-payload-issues",
        children: "Large payload issues"
      }), "\n", _jsxs(_components.p, {
        children: ["Segment returns a ", _jsx(_components.code, {
          children: "400 Bad Request"
        }), " error if your batch payload exceeds the limitations. You can find more information in ", _jsx(_components.a, {
          href: "https://segment.com/docs/connections/sources/catalog/libraries/server/http-api/#errors",
          children: "Segment's documentation"
        }), ". Adjust your batch configuration accordingly."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
